import { QueryResult } from '@apollo/client';
import IconAdd from '@apollo/icons/default/IconAdd.svg';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import { Button, Code, Heading, IconButton } from '@apollo/orbit';
import { groupBy, orderBy } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useState } from 'react';

import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { OperationCollectionsSection } from '../OperationCollectionsSection';

import { shortcodesConfig } from './shortcodesConfig';

const ShortcodeOption = ({
  name,
  shortcodeId,
  onClick,
}: {
  name: string;
  shortcodeId: string;
  onClick?: () => void | null;
}) => (
  <div key={shortcodeId} className="mb-2">
    <h3 className="mb-1 text-sm font-normal capitalize">{name}</h3>
    <div className="flex items-center">
      {onClick && (
        <Tooltip label={`Insert ${name}`}>
          <IconButton
            role="button"
            aria-label={`Insert ${name}`}
            variant="hidden"
            className="-ml-2 mr-1"
            onClick={onClick}
            size="sm"
            icon={<IconAdd />}
          />
        </Tooltip>
      )}
      <Code>
        {'{{ '} {shortcodeId} {' }}'}
      </Code>
    </div>
  </div>
);

const ShortcodeAdder = ({
  name,
  shortcodeId,
  editor,
}: {
  name: string;
  shortcodeId: string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
}) => {
  const trackCustomEvent = useTrackCustomEvent();
  const editorPosition = useDerivedEditorValue({
    editor,
    getValue: React.useCallback(
      (_, position: monacoEditor.Position | undefined) => position,
      [],
    ),
    withPosition: true,
  });
  return (
    <ShortcodeOption
      onClick={() => {
        if (editorPosition) {
          const range = new monacoEditor.Range(
            editorPosition.lineNumber,
            editorPosition.column,
            editorPosition.lineNumber,
            editorPosition.column,
          );
          const op = {
            range,
            text: `{{ ${shortcodeId} }}`,
            forceMoveMarkers: true,
          };
          editor.executeEdits('Shortcodes add', [op]);
          editor.focus();
        }
        trackCustomEvent({
          category: 'README Shortcodes',
          action: `Add shortcode for ${name}`,
        });
      }}
      shortcodeId={shortcodeId}
      name={name}
    />
  );
};

export const ShortcodesMenu = ({
  editor,
  onClose,
  style,
  operationCollectionsQuery,
  isProposalRoute,
}: {
  editor?: monacoEditor.editor.IStandaloneCodeEditor | null;
  onClose?: () => void | null;
  style?: object;
  operationCollectionsQuery: QueryResult<
    GraphQLTypes.OperationCollectionReadMeQuery,
    GraphQLTypes.Exact<{
      graphId: string;
      variant: string;
    }>
  >;
  isProposalRoute: boolean;
}) => {
  const [expandOperationCollections, setExpandOperationCollections] =
    useState(false);
  const [expandGraphDetails, setExpandGraphDetails] = useState(true);
  const [expandGraphUrls, setExpandGraphUrls] = useState(true);
  const grouped = groupBy(shortcodesConfig, ({ id, displayContext }) =>
    (displayContext === 'not-proposals' && isProposalRoute) ||
    (displayContext === 'proposals' && !isProposalRoute)
      ? 'hidden'
      : id.startsWith('graph.url')
        ? 'graphUrls'
        : 'graphDetails',
  );

  const variantSharedCollectionsWithOperations = orderBy(
    operationCollectionsQuery.data?.service?.variant?.operationCollections.filter(
      (collection) => collection.isShared && collection.operations.length > 0,
    ),
    [
      (collection) => (collection.isFavorite ? -1 : 1),
      (collection) => collection.name,
    ],
  );

  return (
    <div
      style={style}
      className="overflow-auto border-r border-primary px-6 py-4"
    >
      <div className="mb-2 flex items-center justify-between">
        <Heading className="flex items-center gap-2">
          <IconDocument className="text-icon-primary" />
          Graph shortcodes
        </Heading>

        {onClose && (
          <IconButton
            onClick={onClose}
            role="button"
            aria-label="Close Graph Shortcodes Menu"
            variant="hidden"
            size="sm"
            className="-mr-3"
          >
            <IconClose />
          </IconButton>
        )}
      </div>
      <p className="mb-4 text-secondary">
        Use these shortcodes in your README markdown for graph specific
        variables.
      </p>
      {variantSharedCollectionsWithOperations &&
        variantSharedCollectionsWithOperations.length > 0 && (
          <div className="pb-3">
            <Button
              className="-ml-3"
              variant="hidden"
              size="sm"
              onClick={() =>
                setExpandOperationCollections(!expandOperationCollections)
              }
              leftIcon={
                expandOperationCollections ? (
                  <IconChevronDown className="text-icon-primary" />
                ) : (
                  <IconChevronRight className="text-icon-primary" />
                )
              }
            >
              Operation collections
            </Button>
            {expandOperationCollections && (
              <OperationCollectionsSection
                editor={editor}
                variantSharedCollectionsWithOperations={
                  variantSharedCollectionsWithOperations
                }
                loading={operationCollectionsQuery.loading}
              />
            )}
          </div>
        )}
      <div className="pb-3">
        <Button
          className="-ml-3"
          variant="hidden"
          size="sm"
          onClick={() => setExpandGraphDetails(!expandGraphDetails)}
          leftIcon={
            expandGraphDetails ? (
              <IconChevronDown className="text-icon-primary" />
            ) : (
              <IconChevronRight className="text-icon-primary" />
            )
          }
        >
          Graph details
        </Button>
        {expandGraphDetails &&
          grouped?.graphDetails?.map(({ id, name }) =>
            editor ? (
              <ShortcodeAdder
                key={name}
                name={name}
                shortcodeId={id}
                editor={editor}
              />
            ) : (
              <ShortcodeOption key={name} name={name} shortcodeId={id} />
            ),
          )}
      </div>

      <div className="pb-3">
        <Button
          className="-ml-3"
          variant="hidden"
          size="sm"
          onClick={() => setExpandGraphUrls(!expandGraphUrls)}
          leftIcon={
            expandGraphUrls ? (
              <IconChevronDown className="text-icon-primary" />
            ) : (
              <IconChevronRight className="text-icon-primary" />
            )
          }
        >
          Graph URLs
        </Button>
        {expandGraphUrls &&
          grouped?.graphUrls?.map(({ id, name }) =>
            editor ? (
              <ShortcodeAdder
                key={name}
                name={name}
                shortcodeId={id}
                editor={editor}
              />
            ) : (
              <ShortcodeOption key={name} name={name} shortcodeId={id} />
            ),
          )}
      </div>
    </div>
  );
};
